// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { type MedplumClient, resolveId } from '@medplum/core';
import type {
  CoverageEligibilityResponse,
  CoverageEligibilityResponseInsuranceItem,
  CoverageEligibilityResponseInsuranceItemBenefit,
  Organization,
  Patient,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { NPI_SYSTEM, STEDI_PAYER_NETWORK_SYSTEM } from '../constants/eligibility';
import { patientDisplayName } from '../utils/patient-display';

const BENEFIT_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/benefit-type';

export interface BenefitRow {
  id: string;
  type: string;
  network: string;
  coverage: string;
  terms: string;
}

export interface BenefitGroup {
  label: 'Co-Insurance' | 'Deductible' | 'Out of Pocket Maximum';
  rows: BenefitRow[];
}

export interface CoverageEligibilityDetail {
  dateChecked: string;
  isActive: boolean;
  subscriber: { name: string; mrn: string };
  payor: { name: string; id: string };
  provider: { name: string; npi: string };
  stcLabel: string;
  estimatedResponsibility: string;
  benefitGroups: BenefitGroup[];
}

export interface UseCoverageEligibilityDetailReturn {
  detail: CoverageEligibilityDetail | null;
  loading: boolean;
  error: string | null;
}

const BENEFIT_GROUP_CODES: Partial<Record<string, BenefitGroup['label']>> = {
  coinsurance: 'Co-Insurance',
  deductible: 'Deductible',
  'out-of-pocket': 'Out of Pocket Maximum',
};

const GROUP_ORDER: BenefitGroup['label'][] = ['Co-Insurance', 'Deductible', 'Out of Pocket Maximum'];

function getPatientMrn(patient: Patient): string {
  return patient.identifier?.find((id) => id.type?.coding?.some((c) => c.code === 'MR'))?.value ?? '—';
}

function getPayorIdentifier(org: Organization): string {
  return org.identifier?.find((id) => id.system === STEDI_PAYER_NETWORK_SYSTEM)?.value ?? org.name ?? '—';
}

function getProviderNpi(org: Organization): string {
  return org.identifier?.find((id) => id.system === NPI_SYSTEM)?.value ?? '—';
}

function formatCoverage(
  benefit: CoverageEligibilityResponseInsuranceItemBenefit,
  item: CoverageEligibilityResponseInsuranceItem
): string {
  const termDisplay = item.term?.coding?.[0]?.display ?? item.term?.coding?.[0]?.code;
  const suffix = termDisplay ? ` / ${termDisplay}` : '';

  if (benefit.allowedString != null) {
    return `${benefit.allowedString}${suffix}`;
  }
  if (benefit.allowedUnsignedInt != null) {
    return `${benefit.allowedUnsignedInt}%${suffix}`;
  }
  if (benefit.allowedMoney?.value != null) {
    return `$${benefit.allowedMoney.value.toLocaleString('en-US')}${suffix}`;
  }
  return '—';
}

function buildBenefitGroups(cer: CoverageEligibilityResponse): BenefitGroup[] {
  const items = cer.insurance?.[0]?.item ?? [];
  const groupMap = new Map<BenefitGroup['label'], BenefitRow[]>();

  items.forEach((item, itemIdx) => {
    (item.benefit ?? []).forEach((benefit, benefitIdx) => {
      const typeCode = benefit.type?.coding?.find((c) => c.system === BENEFIT_TYPE_SYSTEM)?.code;
      const groupLabel = typeCode ? BENEFIT_GROUP_CODES[typeCode] : undefined;
      if (!groupLabel) {
        return;
      }

      const row: BenefitRow = {
        id: `${groupLabel}-${itemIdx}-${benefitIdx}`,
        type: item.unit?.coding?.[0]?.display ?? '—',
        network: item.network?.coding?.[0]?.display ?? '—',
        coverage: formatCoverage(benefit, item),
        terms: item.description ?? item.term?.coding?.[0]?.display ?? '—',
      };

      const existing = groupMap.get(groupLabel);
      if (existing) {
        existing.push(row);
      } else {
        groupMap.set(groupLabel, [row]);
      }
    });
  });

  return GROUP_ORDER.filter((label) => groupMap.has(label)).map((label) => ({
    label,
    rows: groupMap.get(label) ?? [],
  }));
}

function getStcLabel(cer: CoverageEligibilityResponse): string {
  const cat = cer.insurance?.[0]?.item?.[0]?.category?.coding?.[0];
  if (!cat) {
    return '—';
  }
  return `STC ${cat.code ?? ''} - ${cat.display ?? ''}`.trim();
}

function getEstimatedResponsibility(cer: CoverageEligibilityResponse): string {
  const items = cer.insurance?.[0]?.item ?? [];
  for (const item of items) {
    for (const benefit of item.benefit ?? []) {
      if (benefit.type?.coding?.some((c) => c.system === BENEFIT_TYPE_SYSTEM && c.code === 'copay')) {
        const val = benefit.allowedMoney?.value;
        if (val != null) {
          return `$${val.toFixed(2)}`;
        }
      }
    }
  }
  return '—';
}

async function readRelatedPatient(medplum: MedplumClient, id: string | undefined): Promise<Patient | undefined> {
  if (!id) {
    return undefined;
  }
  try {
    return await medplum.readResource('Patient', id);
  } catch (err) {
    console.error('[useCoverageEligibilityDetail] Failed to load Patient', { id, err });
    return undefined;
  }
}

async function readRelatedOrganization(
  medplum: MedplumClient,
  id: string | undefined,
  role: 'insurer' | 'provider'
): Promise<Organization | undefined> {
  if (!id) {
    return undefined;
  }
  try {
    return await medplum.readResource('Organization', id);
  } catch (err) {
    console.error(`[useCoverageEligibilityDetail] Failed to load Organization (${role})`, { id, err });
    return undefined;
  }
}

export function useCoverageEligibilityDetail(cerRef: string | null): UseCoverageEligibilityDetailReturn {
  const medplum = useMedplum();
  const [detail, setDetail] = useState<CoverageEligibilityDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!cerRef) {
      setDetail(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setDetail(null);

    try {
      const cer = await medplum.readResource('CoverageEligibilityResponse', cerRef);

      const patientId = resolveId(cer.patient);
      const insurerId = resolveId(cer.insurer);
      const requestorRef = cer.requestor?.reference;
      const providerOrgId = requestorRef?.startsWith('Organization/') ? resolveId(cer.requestor) : undefined;

      const [patient, insurer, providerOrg] = await Promise.all([
        readRelatedPatient(medplum, patientId),
        readRelatedOrganization(medplum, insurerId, 'insurer'),
        readRelatedOrganization(medplum, providerOrgId, 'provider'),
      ]);

      setDetail({
        dateChecked: cer.created ?? '',
        isActive: cer.outcome === 'complete' && cer.insurance?.[0]?.inforce === true,
        subscriber: {
          name: patientDisplayName(patient, cer.patient),
          mrn: patient ? getPatientMrn(patient) : '—',
        },
        payor: {
          name: insurer?.name ?? cer.insurer?.display ?? '—',
          id: insurer ? getPayorIdentifier(insurer) : '—',
        },
        provider: {
          name: providerOrg?.name ?? '—',
          npi: providerOrg ? getProviderNpi(providerOrg) : '—',
        },
        stcLabel: getStcLabel(cer),
        estimatedResponsibility: getEstimatedResponsibility(cer),
        benefitGroups: buildBenefitGroups(cer),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load eligibility details');
    } finally {
      setLoading(false);
    }
  }, [medplum, cerRef]);

  useEffect(() => {
    fetchDetail().catch(console.error);
  }, [fetchDetail]);

  return { detail, loading, error };
}
